import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Award, 
  FileText, 
  Download, 
  Lock, 
  Users, 
  Clock, 
  Calendar,
  History,
  Loader2,
  Search,
  ChevronRight,
  Crown,
  Star,
  Package,
  FileArchive,
  CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CertificatiPersonalizzati() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [volontari, setVolontari] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVolontario, setSelectedVolontario] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Form state
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [motivazione, setMotivazione] = useState('');
  const [testoPersonalizzato, setTestoPersonalizzato] = useState('');
  
  // Batch state
  const [batchTemplate, setBatchTemplate] = useState(null);
  const [batchAnno, setBatchAnno] = useState(new Date().getFullYear());
  const [batchPreview, setBatchPreview] = useState(null);
  const [soloConPresenze, setSoloConPresenze] = useState(true);
  const [generatingBatch, setGeneratingBatch] = useState(false);
  const [loadingBatchPreview, setLoadingBatchPreview] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchVolontari();
    fetchHistory();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/certificates/templates`, { credentials: 'include', headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchVolontari = async () => {
    try {
      const res = await fetch(`${API}/api/volontari`, { credentials: 'include', headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setVolontari(data);
      }
    } catch (error) {
      console.error('Error fetching volontari:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/api/certificates/history?limit=20`, { credentials: 'include', headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchPreview = async (volontarioId, templateId) => {
    try {
      const res = await fetch(
        `${API}/api/certificates/volontario/${volontarioId}/preview?template_id=${templateId}&anno=${anno}`,
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
        setShowPreview(true);
      }
    } catch (error) {
      toast.error('Errore nel caricamento anteprima');
    }
  };

  const generateCertificate = async () => {
    if (!selectedVolontario || !selectedTemplate) {
      toast.error('Seleziona un volontario e un template');
      return;
    }

    setGenerating(true);
    try {
      const params = new URLSearchParams({
        volontario_id: selectedVolontario.volontario_id,
        anno: anno.toString()
      });
      
      if (motivazione) params.append('motivazione', motivazione);
      if (testoPersonalizzato) params.append('testo_personalizzato', testoPersonalizzato);

      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/api/certificates/genera/${selectedTemplate.id}?${params.toString()}`,
        { 
          method: 'POST',
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Certificato_${selectedTemplate.id}_${selectedVolontario.cognome}_${anno}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('Certificato generato con successo!');
        fetchHistory();
        setShowPreview(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella generazione');
      }
    } catch (error) {
      toast.error('Errore nella generazione del certificato');
    } finally {
      setGenerating(false);
    }
  };

  // Batch functions
  const fetchBatchPreview = async (templateId, yearVal, onlyWithPresenze) => {
    if (!templateId) return;
    
    setLoadingBatchPreview(true);
    try {
      const params = new URLSearchParams({
        template_id: templateId,
        anno: yearVal.toString(),
        solo_con_presenze: onlyWithPresenze.toString()
      });
      
      const res = await fetch(
        `${API}/api/certificates/batch/preview?${params.toString()}`,
        { credentials: 'include', headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        const data = await res.json();
        setBatchPreview(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nel caricamento anteprima');
      }
    } catch (error) {
      toast.error('Errore nel caricamento anteprima batch');
    } finally {
      setLoadingBatchPreview(false);
    }
  };

  const generateBatchCertificates = async () => {
    if (!batchTemplate) {
      toast.error('Seleziona un template');
      return;
    }

    setGeneratingBatch(true);
    try {
      const params = new URLSearchParams({
        template_id: batchTemplate.id,
        anno: batchAnno.toString(),
        solo_con_presenze: soloConPresenze.toString()
      });

      const res = await fetch(
        `${API}/api/certificates/batch/genera?${params.toString()}`,
        { 
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );

      if (res.ok) {
        const blob = await res.blob();
        const generatedCount = res.headers.get('X-Certificates-Generated') || '0';
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Certificati_${batchTemplate.id}_${batchAnno}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success(`Generati ${generatedCount} certificati con successo!`);
        fetchHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella generazione batch');
      }
    } catch (error) {
      toast.error('Errore nella generazione batch');
    } finally {
      setGeneratingBatch(false);
    }
  };

  const filteredVolontari = volontari.filter(v => 
    `${v.nome} ${v.cognome}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.codice_fiscale?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const userPlan = user?.plan || 'free';
  // Premium, Governance, Pro, Enterprise, and Convenzioni plans can access certificates
  const canAccessCertificates = ['premium', 'governance', 'pro', 'enterprise', 'convenzioni'].includes(userPlan);

  if (!canAccessCertificates) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Certificati Personalizzati</h1>
          <p className="text-slate-500 mt-1">Genera attestati e certificati per i tuoi volontari</p>
        </div>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-amber-100 rounded-full">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-800">Funzionalità Premium</h3>
                <p className="text-amber-700 mt-1">
                  I certificati personalizzati sono disponibili a partire dal piano Premium.
                </p>
              </div>
              <Button 
                onClick={() => window.location.href = '/pricing'}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Crown className="w-4 h-4 mr-2" />
                Scopri i Piani
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Certificati Personalizzati</h1>
          <p className="text-slate-500 mt-1">Genera attestati e certificati per i tuoi volontari</p>
        </div>
        <Badge variant={['pro', 'enterprise', 'convenzioni'].includes(userPlan) ? 'default' : 'secondary'} className="uppercase">
          Piano {userPlan}
        </Badge>
      </div>

      <Tabs defaultValue="genera" className="space-y-4">
        <TabsList>
          <TabsTrigger value="genera" data-testid="tab-genera">
            <Award className="w-4 h-4 mr-2" />
            Genera Certificato
          </TabsTrigger>
          <TabsTrigger value="batch" data-testid="tab-batch">
            <Package className="w-4 h-4 mr-2" />
            Generazione Batch
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="w-4 h-4 mr-2" />
            Template Disponibili
          </TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico">
            <History className="w-4 h-4 mr-2" />
            Storico
          </TabsTrigger>
        </TabsList>

        {/* GENERA CERTIFICATO */}
        <TabsContent value="genera" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Configura Certificato</CardTitle>
                <CardDescription>Seleziona volontario, template e personalizza</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Anno */}
                <div className="space-y-2">
                  <Label>Anno di riferimento</Label>
                  <Select value={anno.toString()} onValueChange={(v) => setAnno(parseInt(v))}>
                    <SelectTrigger data-testid="select-anno">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const y = new Date().getFullYear() - i;
                        return <SelectItem key={y} value={y.toString()}>{y}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Search Volontario */}
                <div className="space-y-2">
                  <Label>Cerca Volontario</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Nome, cognome o codice fiscale..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-volontario"
                    />
                  </div>
                </div>

                {/* Volontari List */}
                {searchTerm && filteredVolontari.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {filteredVolontari.slice(0, 10).map((v) => (
                      <div
                        key={v.volontario_id}
                        className={`p-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${
                          selectedVolontario?.volontario_id === v.volontario_id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                        }`}
                        onClick={() => setSelectedVolontario(v)}
                        data-testid={`volontario-${v.volontario_id}`}
                      >
                        <p className="font-medium">{v.nome} {v.cognome}</p>
                        <p className="text-sm text-slate-500">{v.codice_fiscale}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Selected Volontario */}
                {selectedVolontario && (
                  <Alert>
                    <Users className="w-4 h-4" />
                    <AlertDescription>
                      <strong>Selezionato:</strong> {selectedVolontario.nome} {selectedVolontario.cognome}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Template Selection */}
                <div className="space-y-2">
                  <Label>Tipo Certificato</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {templates.map((t) => (
                      <div
                        key={t.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          t.locked 
                            ? 'opacity-50 cursor-not-allowed bg-slate-50' 
                            : selectedTemplate?.id === t.id 
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' 
                              : 'hover:border-slate-300'
                        }`}
                        onClick={() => !t.locked && setSelectedTemplate(t)}
                        data-testid={`template-${t.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {t.nome}
                              {t.required_plan === 'pro' && (
                                <Badge variant="outline" className="text-xs">PRO</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{t.descrizione}</p>
                          </div>
                          {t.locked ? (
                            <Lock className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Custom Fields based on template */}
                {selectedTemplate?.id === 'merito' && (
                  <div className="space-y-2">
                    <Label>Motivazione del Merito</Label>
                    <Textarea
                      placeholder="Es: per l'eccezionale dedizione dimostrata..."
                      value={motivazione}
                      onChange={(e) => setMotivazione(e.target.value)}
                      data-testid="input-motivazione"
                    />
                  </div>
                )}

                {selectedTemplate?.id === 'formazione' && (
                  <div className="space-y-2">
                    <Label>Nome del Corso/Formazione</Label>
                    <Input
                      placeholder="Es: Corso base primo soccorso"
                      value={testoPersonalizzato}
                      onChange={(e) => setTestoPersonalizzato(e.target.value)}
                      data-testid="input-corso"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => selectedVolontario && selectedTemplate && fetchPreview(selectedVolontario.volontario_id, selectedTemplate.id)}
                    disabled={!selectedVolontario || !selectedTemplate}
                    data-testid="btn-preview"
                  >
                    Anteprima Dati
                  </Button>
                  <Button
                    onClick={generateCertificate}
                    disabled={!selectedVolontario || !selectedTemplate || generating}
                    className="flex-1"
                    data-testid="btn-genera"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generazione...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Genera PDF
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Right: Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Anteprima</CardTitle>
                <CardDescription>Dati che verranno inclusi nel certificato</CardDescription>
              </CardHeader>
              <CardContent>
                {previewData ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                      <h4 className="font-semibold text-slate-700">Volontario</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-slate-500">Nome:</span>
                        <span className="font-medium">{previewData.volontario.nome_completo}</span>
                        <span className="text-slate-500">Codice Fiscale:</span>
                        <span className="font-medium">{previewData.volontario.codice_fiscale || '-'}</span>
                        <span className="text-slate-500">Data Nascita:</span>
                        <span className="font-medium">{previewData.volontario.data_nascita || '-'}</span>
                      </div>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                      <h4 className="font-semibold text-blue-700">Statistiche Anno {previewData.statistiche.anno}</h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-blue-600">{previewData.statistiche.ore_totali}</p>
                          <p className="text-xs text-blue-500">Ore totali</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-blue-600">{previewData.statistiche.eventi_partecipati}</p>
                          <p className="text-xs text-blue-500">Eventi</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-blue-600">{previewData.statistiche.presenze_registrate}</p>
                          <p className="text-xs text-blue-500">Presenze</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-green-50 rounded-lg space-y-2">
                      <h4 className="font-semibold text-green-700">Ente</h4>
                      <p className="font-medium">{previewData.ente.denominazione}</p>
                      {previewData.ente.has_logo && (
                        <Badge variant="outline" className="text-green-600">
                          <Star className="w-3 h-3 mr-1" />
                          Logo caricato
                        </Badge>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-slate-400 border-2 border-dashed rounded-lg">
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Seleziona volontario e template,<br/>poi clicca "Anteprima Dati"</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* BATCH GENERATION */}
        <TabsContent value="batch" className="space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <Package className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              Genera tutti i certificati per i volontari attivi in un unico file ZIP. 
              Ideale per la fine dell'anno o per eventi speciali.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Config */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileArchive className="w-5 h-5 text-purple-600" />
                  Configurazione Batch
                </CardTitle>
                <CardDescription>Seleziona template e anno per la generazione massiva</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Anno */}
                <div className="space-y-2">
                  <Label>Anno di riferimento</Label>
                  <Select 
                    value={batchAnno.toString()} 
                    onValueChange={(v) => {
                      setBatchAnno(parseInt(v));
                      if (batchTemplate) {
                        fetchBatchPreview(batchTemplate.id, parseInt(v), soloConPresenze);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="batch-select-anno">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const y = new Date().getFullYear() - i;
                        return <SelectItem key={y} value={y.toString()}>{y}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Template Selection */}
                <div className="space-y-2">
                  <Label>Tipo Certificato</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {templates.filter(t => !t.locked && ['partecipazione', 'ore_volontariato'].includes(t.id)).map((t) => (
                      <div
                        key={t.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          batchTemplate?.id === t.id 
                            ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500' 
                            : 'hover:border-slate-300'
                        }`}
                        onClick={() => {
                          setBatchTemplate(t);
                          fetchBatchPreview(t.id, batchAnno, soloConPresenze);
                        }}
                        data-testid={`batch-template-${t.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{t.nome}</p>
                            <p className="text-sm text-slate-500">{t.descrizione}</p>
                          </div>
                          {batchTemplate?.id === t.id && (
                            <CheckCircle2 className="w-5 h-5 text-purple-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Filter Option */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="font-medium">Solo volontari con presenze</Label>
                    <p className="text-sm text-slate-500">Escludi volontari senza ore registrate</p>
                  </div>
                  <Switch 
                    checked={soloConPresenze}
                    onCheckedChange={(checked) => {
                      setSoloConPresenze(checked);
                      if (batchTemplate) {
                        fetchBatchPreview(batchTemplate.id, batchAnno, checked);
                      }
                    }}
                    data-testid="batch-switch-presenze"
                  />
                </div>

                {/* Generate Button */}
                <Button
                  onClick={generateBatchCertificates}
                  disabled={!batchTemplate || !batchPreview || batchPreview.volontari_da_generare === 0 || generatingBatch}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="btn-genera-batch"
                >
                  {generatingBatch ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generazione in corso...
                    </>
                  ) : (
                    <>
                      <FileArchive className="w-4 h-4 mr-2" />
                      Genera {batchPreview?.volontari_da_generare || 0} Certificati (ZIP)
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Right: Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Anteprima Batch</CardTitle>
                <CardDescription>Volontari che riceveranno il certificato</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingBatchPreview ? (
                  <div className="h-64 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : batchPreview ? (
                  <div className="space-y-4">
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-700">{batchPreview.volontari_totali}</p>
                        <p className="text-xs text-slate-500">Totale Attivi</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{batchPreview.volontari_con_presenze}</p>
                        <p className="text-xs text-green-500">Con Presenze</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">{batchPreview.volontari_da_generare}</p>
                        <p className="text-xs text-purple-500">Da Generare</p>
                      </div>
                    </div>

                    {/* Volontari List */}
                    {batchPreview.volontari.length > 0 ? (
                      <div className="border rounded-lg max-h-64 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nome</TableHead>
                              <TableHead className="text-right">Ore</TableHead>
                              <TableHead className="text-right">Eventi</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {batchPreview.volontari.map((v) => (
                              <TableRow key={v.volontario_id}>
                                <TableCell className="font-medium">{v.nome_completo}</TableCell>
                                <TableCell className="text-right">{v.ore_anno}</TableCell>
                                <TableCell className="text-right">{v.eventi_partecipati}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {batchPreview.volontari_da_generare > 50 && (
                          <p className="text-center text-sm text-slate-500 py-2">
                            ...e altri {batchPreview.volontari_da_generare - 50} volontari
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>{batchPreview.message}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-slate-400 border-2 border-dashed rounded-lg">
                    <div className="text-center">
                      <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Seleziona un template<br/>per vedere l'anteprima</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TEMPLATES */}
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((t) => (
              <Card key={t.id} className={t.locked ? 'opacity-60' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {t.nome}
                      {t.required_plan === 'pro' && (
                        <Badge>PRO</Badge>
                      )}
                    </CardTitle>
                    {t.locked ? (
                      <Lock className="w-5 h-5 text-slate-400" />
                    ) : (
                      <Badge variant="outline" className="text-green-600">Disponibile</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm">{t.descrizione}</p>
                  <div className="mt-4 flex gap-2">
                    <div 
                      className="w-6 h-6 rounded-full" 
                      style={{ backgroundColor: t.colore_primario }}
                      title="Colore primario"
                    />
                    <div 
                      className="w-6 h-6 rounded-full" 
                      style={{ backgroundColor: t.colore_secondario }}
                      title="Colore secondario"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* STORICO */}
        <TabsContent value="storico" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Storico Generazioni</CardTitle>
              <CardDescription>Ultimi certificati generati</CardDescription>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Dettagli</TableHead>
                      <TableHead>Utente</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">
                          {new Date(h.timestamp).toLocaleDateString('it-IT', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{h.operazione}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{h.dettagli}</TableCell>
                        <TableCell className="text-sm">{h.user_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nessun certificato generato</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
